/**
 * Chat-attachment API client.
 *
 * Frontend talks to the auth-scoped attachment routes the backend ships
 * under `/dashboard/attachments` — uploads land in R2 and come back with an
 * `r2_key` we can hand straight to the fill agent via
 * `FillRequest.context_files`. The shape is kept narrow and stable so the
 * UI can keep working while the backend's preview pipeline grows (more
 * mime types, smarter snippets, etc.).
 *
 * These endpoints are not yet exposed by the generated `schema.d.ts` on
 * this branch, so we hand-type the wire format here and use `authFetch`
 * directly (matching the existing document-upload path in queries.ts).
 * Tests mock this module rather than the lower-level fetcher so they stay
 * focused on UX behaviour rather than HTTP plumbing.
 */
import { API_BASE_URL, ApiError, AuthError, authFetch } from './client'

export const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024

/**
 * Maximum number of chat-context attachments per fill request. Must match
 * ``MAX_CONTEXT_FILES_PER_FILL`` on the backend (``app/routers/documents.py``);
 * the server is the authoritative gate but enforcing it here gives the user
 * instant feedback instead of an opaque 413 after they hit send.
 */
export const ATTACHMENT_MAX_COUNT = 8

/**
 * Maximum instruction length the chat input accepts before submit. Must
 * match ``MAX_INSTRUCTION_CHARS`` on the backend — a longer prompt is
 * rejected with 413 there, so we refuse early to avoid the round-trip.
 */
export const CHAT_INSTRUCTION_MAX_CHARS = 8_000

export const ATTACHMENT_ACCEPTED_MIME_PREFIXES = [
  'image/',
  'text/',
  'application/pdf',
  'application/json',
] as const

/** Common file extensions whose `file.type` browsers sometimes leave blank. */
export const ATTACHMENT_ACCEPTED_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.bmp',
  '.svg',
  '.pdf',
  '.txt',
  '.md',
  '.json',
  '.csv',
  '.log',
] as const

/**
 * What the user sees when we have to refuse a file before even uploading.
 * Kept in one place so the chat input, dropzone, and tests don't drift.
 */
export const ATTACHMENT_VALIDATION_MESSAGES = {
  TOO_LARGE: 'File is too large. Attachments must be 20MB or smaller.',
  UNSUPPORTED_TYPE:
    'That file type is not supported. Attach an image, PDF, or text file.',
  EMPTY: 'That file is empty.',
  TOO_MANY: `Up to ${ATTACHMENT_MAX_COUNT} attachments per message. Remove one before adding more.`,
} as const

export type AttachmentKind =
  | 'image'
  | 'screenshot'
  | 'pdf'
  | 'text'
  | 'other'

export interface AttachmentRead {
  uuid: string
  filename: string
  mime_type: string
  byte_size: number
  kind: AttachmentKind
  r2_key: string
  created_at: string
}

export interface AttachmentImagePreview {
  kind: 'image'
  mime_type: string
  filename: string
  byte_size: number
  width: number | null
  height: number | null
  /** Presigned R2 URL for the original (or a downscaled thumbnail). */
  url: string
}

export interface AttachmentTextPreview {
  kind: 'text'
  mime_type: string
  filename: string
  byte_size: number
  /** First N chars of the file (server-truncated). */
  snippet: string
  /** Total chars in the file — UI uses this to show "showing X of Y chars". */
  total_chars: number
  truncated: boolean
}

export interface AttachmentUnsupportedPreview {
  kind: 'unsupported'
  mime_type: string
  filename: string
  byte_size: number
  reason: string
}

export type AttachmentPreview =
  | AttachmentImagePreview
  | AttachmentTextPreview
  | AttachmentUnsupportedPreview

export interface UploadAttachmentInput {
  file: File
  /** Optional — flips the server's `kind` to ``screenshot`` for analytics. */
  source?: 'paste' | 'screenshot' | 'file'
  signal?: AbortSignal
}

/**
 * Validate a `File` *before* sending it — gives the user an instant message
 * instead of waiting for the server to bounce a 4xx. The server validates
 * again as the source of truth.
 */
export function validateAttachment(file: File): string | null {
  if (file.size === 0) return ATTACHMENT_VALIDATION_MESSAGES.EMPTY
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return ATTACHMENT_VALIDATION_MESSAGES.TOO_LARGE
  }
  const mime = (file.type || '').toLowerCase()
  if (mime) {
    const okMime = ATTACHMENT_ACCEPTED_MIME_PREFIXES.some((prefix) =>
      mime.startsWith(prefix),
    )
    if (okMime) return null
  }
  // Some browsers (Safari with pasted PNGs from screenshots) hand us a File
  // with an empty type. Fall back to extension matching so we don't reject
  // legit clipboard captures.
  const lower = file.name.toLowerCase()
  const okExt = ATTACHMENT_ACCEPTED_EXTENSIONS.some((ext) =>
    lower.endsWith(ext),
  )
  return okExt ? null : ATTACHMENT_VALIDATION_MESSAGES.UNSUPPORTED_TYPE
}

/**
 * Upload one attachment via the auth-scoped attachments endpoint.
 *
 * Mirrors the existing `useUploadDocument` pattern in queries.ts: hits
 * `authFetch` with a `FormData` body, which handles auth refresh, timeouts,
 * and session-expired routing for free. On 401/403 we throw `AuthError`
 * (so callers can differentiate session-expired from generic network
 * failure); on any other non-2xx we throw `ApiError` carrying the server's
 * error body.
 */
export async function uploadAttachment(
  input: UploadAttachmentInput,
): Promise<AttachmentRead> {
  const { file, source, signal } = input
  const form = new FormData()
  form.append('file', file)
  if (source) form.append('source', source)
  const res = await authFetch(`${API_BASE_URL}/dashboard/attachments`, {
    method: 'POST',
    body: form,
    signal,
  })
  if (!res.ok) {
    const body = await safeJsonResponse(res)
    if (res.status === 401 || res.status === 403) {
      throw new AuthError(
        res.status,
        readDetail(body) ??
          (res.status === 403 ? 'Forbidden' : 'Session expired'),
      )
    }
    throw new ApiError(body)
  }
  return (await res.json()) as AttachmentRead
}

export async function fetchAttachmentPreview(
  uuid: string,
  init?: { signal?: AbortSignal },
): Promise<AttachmentPreview> {
  const res = await authFetch(
    `${API_BASE_URL}/dashboard/attachments/${encodeURIComponent(uuid)}/preview`,
    { method: 'GET', signal: init?.signal },
  )
  if (!res.ok) {
    const body = await safeJsonResponse(res)
    // Mirror uploadAttachment's auth-handling so a 401/403 from the preview
    // route surfaces as AuthError ("Session expired" / "Forbidden") instead
    // of a generic API failure — the dialog displays `err.message`, so the
    // user sees a clear sign-in prompt rather than an opaque server error.
    if (res.status === 401 || res.status === 403) {
      throw new AuthError(
        res.status,
        readDetail(body) ??
          (res.status === 403 ? 'Forbidden' : 'Session expired'),
      )
    }
    throw new ApiError(body)
  }
  return (await res.json()) as AttachmentPreview
}

export async function deleteAttachment(uuid: string): Promise<void> {
  const res = await authFetch(
    `${API_BASE_URL}/dashboard/attachments/${encodeURIComponent(uuid)}`,
    { method: 'DELETE' },
  )
  // 404 is fine — the attachment is already gone, which is the state the
  // caller wanted. Surface other failures so the UI can recover.
  if (!res.ok && res.status !== 404) {
    throw new ApiError(await safeJsonResponse(res))
  }
}

function readDetail(body: unknown): string | null {
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  return null
}

async function safeJsonResponse(res: Response): Promise<unknown> {
  try {
    return await res.clone().json()
  } catch {
    const text = await res.text().catch(() => '')
    return { detail: text || `HTTP ${res.status}` }
  }
}
